const CATALOG_PAGE_SIZE = 24;

const normalizeText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const inferTeam = (title) => {
  const value = normalizeText(title);
  const teams = [
    ['Mercedes-AMG Petronas', ['mercedes']],
    ['Cadillac Formula 1 Team', ['cadillac']],
    ['Audi Revolut F1 Team', ['audi']],
    ['McLaren F1 Team', ['mclaren']],
    ['Scuderia Ferrari', ['ferrari']],
    ['Oracle Red Bull Racing', ['red bull']],
    ['Aston Martin F1 Team', ['aston martin']],
    ['Williams Racing', ['williams']],
    ['BWT Alpine F1 Team', ['alpine']],
    ['Haas F1 Team', ['haas']],
    ['Racing Bulls', ['racing bulls']],
    ['Pirelli Motorsport', ['pirelli']],
  ];

  return teams.find(([, tokens]) => tokens.some((token) => value.includes(token)))?.[0] || 'Por clasificar';
};

const inferDriver = (title) => {
  const value = normalizeText(title);
  const drivers = [
    ['Sergio “Checo” Pérez', ['checo', 'sergio perez']],
    ['Lando Norris', ['lando norris']],
    ['Oscar Piastri', ['oscar piastri']],
    ['Lewis Hamilton', ['lewis hamilton']],
    ['Charles Leclerc', ['charles leclerc']],
    ['George Russell', ['george russell']],
    ['Fernando Alonso', ['fernando alonso']],
    ['Max Verstappen', ['max verstappen']],
    ['Carlos Sainz', ['carlos sainz']],
    ['Oliver Bearman', ['oliver bearman']],
    ['Isack Hadjar', ['isack hadjar']],
    ['Valentino Rossi', ['valentino rossi']],
  ];

  return drivers.find(([, tokens]) => tokens.some((token) => value.includes(token)))?.[0] || 'Por clasificar';
};

const inferEdition = (title) => {
  const value = normalizeText(title);
  if (value.includes('miami')) return 'Miami';
  if (value.includes('monaco')) return 'Mónaco';
  if (value.includes('australia')) return 'Australia';
  if (value.includes('brasil')) return 'Brasil';
  if (value.includes('campeon')) return 'Campeón';
  if (value.includes('retro')) return 'Retro';
  if (value.includes('2026')) return 'Temporada 2026';
  return 'Por clasificar';
};

const createOption = (value) => {
  const option = document.createElement('option');
  option.value = value;
  option.textContent = value;
  return option;
};

class LaPoleCatalog extends HTMLElement {
  connectedCallback() {
    this.manifest = this.#readManifest();
    this.cards = this.#normalizeCards(this.manifest);
    this.visibleCount = CATALOG_PAGE_SIZE;
    this.filters = {
      category: this.dataset.collectionCategory || 'all',
      team: '',
      driver: '',
      edition: '',
      status: '',
      sort: 'relevance',
    };

    this.grid = this.querySelector('[data-lp-catalog-grid]');
    this.emptyState = this.querySelector('[data-lp-catalog-empty]');
    this.more = this.querySelector('[data-lp-catalog-more]');
    this.loadMoreButton = this.querySelector('[data-lp-load-more]');
    this.progress = this.querySelector('[data-lp-catalog-progress]');
    this.resultCount = this.querySelector('[data-lp-results-count]');
    this.status = this.querySelector('[data-lp-catalog-status]');
    this.clearButton = this.querySelector('[data-lp-clear-filters]');

    this.#hydrateFilters();
    this.#restoreStateFromUrl();
    this.#bindEvents();
    this.#render();

    document.documentElement.classList.add('lp-catalog-ready');
  }

  #readManifest() {
    const node = this.querySelector('[data-lp-catalog-manifest]');
    if (!node) return [];

    try {
      const data = JSON.parse(node.textContent);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('La Pole: no se pudo leer el catálogo.', error);
      return [];
    }
  }

  #normalizeCards(records) {
    const seen = new Set();
    const normalized = [];

    for (const record of records) {
      const key = record.groupKey || `${record.productId}-${record.variantId}`;
      if (seen.has(key)) continue;
      seen.add(key);

      const title = record.title || record.productTitle || 'Producto por clasificar';
      const category = record.category || 'por-clasificar';
      const status =
        record.status === 'clasificado' && category !== 'por-clasificar' && !normalizeText(title).includes('por clasificar')
          ? 'clasificado'
          : 'por-clasificar';

      normalized.push({
        ...record,
        key,
        title,
        category,
        status,
        team: record.team || inferTeam(title),
        driver: record.driver || inferDriver(title),
        edition: record.edition || inferEdition(title),
      });
    }

    return normalized;
  }

  #hydrateFilters() {
    const definitions = [
      ['team', 'Todos los equipos'],
      ['driver', 'Todos los pilotos'],
      ['edition', 'Todas las ediciones'],
    ];

    for (const [name, fallbackLabel] of definitions) {
      const select = this.querySelector(`[data-lp-filter="${name}"]`);
      if (!select) continue;

      const values = [...new Set(this.cards.map((card) => card[name]).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, 'es')
      );

      select.replaceChildren(createOption(''));
      select.options[0].textContent = fallbackLabel;
      values.forEach((value) => select.append(createOption(value)));
      select.disabled = values.length === 0;
    }
  }

  #restoreStateFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const page = Number(params.get('catalogo_pagina') || 1);

    this.filters.team = params.get('equipo') || '';
    this.filters.driver = params.get('piloto') || '';
    this.filters.edition = params.get('edicion') || '';
    this.filters.status = params.get('estado') || '';
    this.filters.sort = params.get('orden') || 'relevance';
    this.visibleCount = Math.max(1, page) * CATALOG_PAGE_SIZE;

    const controls = {
      team: this.filters.team,
      driver: this.filters.driver,
      edition: this.filters.edition,
      status: this.filters.status,
      sort: this.filters.sort,
    };

    Object.entries(controls).forEach(([name, value]) => {
      const control = this.querySelector(`[data-lp-filter="${name}"]`);
      if (control && [...control.options].some((option) => option.value === value)) control.value = value;
    });
  }

  #bindEvents() {
    this.querySelectorAll('[data-lp-filter]').forEach((control) => {
      control.addEventListener('change', () => {
        this.filters[control.dataset.lpFilter] = control.value;
        this.visibleCount = CATALOG_PAGE_SIZE;
        this.#syncUrl(false);
        this.#render();
      });
    });

    this.loadMoreButton?.addEventListener('click', () => {
      this.visibleCount += CATALOG_PAGE_SIZE;
      this.#syncUrl(true);
      this.#render({ announce: true });
    });

    this.clearButton?.addEventListener('click', () => {
      this.filters.team = '';
      this.filters.driver = '';
      this.filters.edition = '';
      this.filters.status = '';
      this.filters.sort = 'relevance';
      this.visibleCount = CATALOG_PAGE_SIZE;

      this.querySelectorAll('[data-lp-filter]').forEach((control) => {
        control.value = control.dataset.lpFilter === 'sort' ? 'relevance' : '';
      });

      this.#syncUrl(false);
      this.#render({ announce: true });
    });

    window.addEventListener('popstate', () => {
      this.#restoreStateFromUrl();
      this.#render();
    });
  }

  #syncUrl(push) {
    const url = new URL(window.location.href);
    const values = {
      equipo: this.filters.team,
      piloto: this.filters.driver,
      edicion: this.filters.edition,
      estado: this.filters.status,
      orden: this.filters.sort === 'relevance' ? '' : this.filters.sort,
      catalogo_pagina: this.visibleCount > CATALOG_PAGE_SIZE ? Math.ceil(this.visibleCount / CATALOG_PAGE_SIZE) : '',
    };

    Object.entries(values).forEach(([key, value]) => {
      if (value) url.searchParams.set(key, value);
      else url.searchParams.delete(key);
    });

    window.history[push ? 'pushState' : 'replaceState']({}, '', url);
  }

  #filteredCards() {
    const cards = this.cards.filter((card) => {
      if (this.filters.category !== 'all' && card.category !== this.filters.category) return false;
      if (this.filters.team && card.team !== this.filters.team) return false;
      if (this.filters.driver && card.driver !== this.filters.driver) return false;
      if (this.filters.edition && card.edition !== this.filters.edition) return false;
      if (this.filters.status && card.status !== this.filters.status) return false;
      return true;
    });

    if (this.filters.sort === 'newest') return [...cards].sort((a, b) => Number(b.position) - Number(a.position));
    if (this.filters.sort === 'price-asc') return [...cards].sort((a, b) => Number(a.price) - Number(b.price));
    if (this.filters.sort === 'price-desc') return [...cards].sort((a, b) => Number(b.price) - Number(a.price));
    return cards;
  }

  #render({ announce = false } = {}) {
    if (!this.grid) return;

    const matches = this.#filteredCards();
    const visible = matches.slice(0, this.visibleCount);
    const fragment = document.createDocumentFragment();

    visible.forEach((card, index) => fragment.append(this.#createCard(card, index)));
    this.grid.replaceChildren(fragment);

    this.emptyState?.toggleAttribute('hidden', matches.length !== 0);
    const hasMore = visible.length < matches.length;
    this.more?.toggleAttribute('hidden', !hasMore);
    if (this.loadMoreButton) {
      this.loadMoreButton.hidden = !hasMore;
    }
    if (this.progress) this.progress.textContent = `Mostrando ${visible.length} de ${matches.length} piezas`;

    if (this.resultCount) this.resultCount.textContent = `${matches.length} ${matches.length === 1 ? 'pieza' : 'piezas'}`;
    document.querySelectorAll('[data-lp-catalog-count]').forEach((node) => {
      node.textContent = `${matches.length} ${matches.length === 1 ? 'pieza' : 'piezas'}`;
    });

    const filtersActive = Boolean(
      this.filters.team || this.filters.driver || this.filters.edition || this.filters.status || this.filters.sort !== 'relevance'
    );
    if (this.clearButton) this.clearButton.hidden = !filtersActive;

    if (announce && this.status) {
      this.status.textContent = `${visible.length} de ${matches.length} piezas visibles.`;
    }
  }

  #createCard(card, index) {
    const item = document.createElement('li');
    item.className = 'product-grid__item product-grid__item--linked-variant';
    item.dataset.productId = `${card.productId}-${card.variantId}`;

    const article = document.createElement('article');
    article.className = 'lp-product-tile lp-product-tile--linked-variant';

    const mediaLink = document.createElement('a');
    mediaLink.className = 'lp-product-tile__media';
    mediaLink.href = card.url;
    mediaLink.setAttribute('aria-label', card.title);

    if (card.image) {
      const image = document.createElement('img');
      image.className = 'lp-product-tile__image';
      image.src = card.image;
      image.alt = card.imageAlt || card.title;
      image.width = 720;
      image.height = 900;
      image.decoding = 'async';
      image.loading = index < 4 ? 'eager' : 'lazy';
      image.fetchPriority = index < 2 ? 'high' : 'auto';
      mediaLink.append(image);
    } else {
      const placeholder = document.createElement('div');
      placeholder.className = 'lp-product-tile__placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      mediaLink.append(placeholder);
    }

    if (!card.available) {
      const badge = document.createElement('span');
      badge.className = 'lp-product-tile__badge';
      badge.textContent = 'Agotado';
      mediaLink.append(badge);
    }

    if (card.promoEligible) {
      const promoBadge = document.createElement('span');
      promoBadge.className = 'lp-promo-badge';
      promoBadge.textContent = '3×2';
      mediaLink.append(promoBadge);
    }

    const meta = document.createElement('div');
    meta.className = 'lp-product-tile__meta';

    const copy = document.createElement('div');
    const type = document.createElement('p');
    type.className = 'lp-product-tile__type';
    type.textContent = card.typeLabel || 'Por clasificar';

    const heading = document.createElement('h3');
    heading.className = 'lp-product-tile__title';
    const titleLink = document.createElement('a');
    titleLink.href = card.url;
    titleLink.textContent = card.title;
    heading.append(titleLink);
    copy.append(type, heading);

    const price = document.createElement('p');
    price.className = 'lp-product-tile__price';
    price.textContent = card.priceFormatted;

    meta.append(copy, price);
    article.append(mediaLink, meta);
    item.append(article);
    return item;
  }
}

if (!customElements.get('lapole-catalog')) {
  customElements.define('lapole-catalog', LaPoleCatalog);
}
